import { t } from 'i18next';
import { get } from 'lodash';
import { RefCallback } from 'react';
import { FieldError, FieldErrors, RefCallBack } from 'react-hook-form';

import { getErrorPaths, getFirstErrorMessage } from './formErrors';

// A step can lock its fields while it validates (Add / Update Line disables them for the whole
// submit, pricing preview included), so a focus request may arrive while its target is disabled,
// or the target may be disabled right after it took the focus (the browser drops the focus then).
// The request stays armed for this long and focuses the target whenever it is enabled again.
export const PENDING_FOCUS_WINDOW_MS = 5000;
const PENDING_FOCUS_INTERVAL_MS = 50;

// Shared by every mounted form: there is one activeElement and focus requests follow a submit, so
// one request is live at a time (a newer one cancels the older). Each submit and focusing trigger
// clears lastFocusTarget first, so it never carries over from an earlier attempt.
let cancelPendingFocus: (() => void) | null = null;
let lastFocusTarget: HTMLElement | null = null;
let isHoldingForPopup = false;

export const clearLastFocusTarget = (): void => {
  lastFocusTarget = null;
};

// An open MUI date picker traps the focus, and once closed it hands the focus back to its opener
// from a timer of its own (PickersPopper, not configurable). A request therefore closes the
// pickers it knows about and waits until no picker popup is on the page before it focuses.
const PICKER_POPUP_SELECTOR = '.MuiPickersPopper-root';
// Bounds the wait for a popup that stays mounted while closed (popperProps.keepMounted).
export const POPUP_HOLD_MS = 1000;

const openPopupClosers = new Set<() => void>();

export const registerOpenPopup = (close: () => void): (() => void) => {
  openPopupClosers.add(close);

  return () => {
    openPopupClosers.delete(close);
  };
};

const closeOpenPopups = () => {
  Array.from(openPopupClosers).forEach((close) => close());
};

const isPickerPopupOnPage = () => null !== document.querySelector(PICKER_POPUP_SELECTOR);

// Focus parked on the page or on a non-tabbable container (the drawer's focus trap) is not a
// choice the user made, so a pending request may take it back.
const isFocusUnclaimed = (active: Element | null) =>
  !active || active === document.body || (active instanceof HTMLElement && active.tabIndex < 0);

export const focusAndCenter = (element?: HTMLElement | null): void => {
  cancelPendingFocus?.();

  lastFocusTarget = element ?? null;

  if (!element) {
    return;
  }

  closeOpenPopups();

  const deadline = Date.now() + PENDING_FOCUS_WINDOW_MS;
  const holdDeadline = Date.now() + POPUP_HOLD_MS;
  let activeAtRequest = document.activeElement;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const stop = () => {
    clearTimeout(timer);
    isHoldingForPopup = false;
    document.removeEventListener('pointerdown', stop, true);
    document.removeEventListener('keydown', stop, true);

    if (cancelPendingFocus === stop) {
      cancelPendingFocus = null;
    }
  };

  const attempt = () => {
    if (isPickerPopupOnPage() && Date.now() < holdDeadline) {
      isHoldingForPopup = true;
      timer = setTimeout(attempt, PENDING_FOCUS_INTERVAL_MS);

      return;
    }

    if (isHoldingForPopup) {
      isHoldingForPopup = false;
      // The picker has just given the focus back to its opener; that is not the user moving on.
      activeAtRequest = document.activeElement;
    }

    const { activeElement } = document;
    const hasFocusMovedOn =
      activeElement !== element &&
      activeElement !== activeAtRequest &&
      !isFocusUnclaimed(activeElement);

    if (!element.isConnected || Date.now() > deadline || hasFocusMovedOn) {
      stop();

      return;
    }

    if (activeElement !== element && !element.matches(':disabled')) {
      element.focus({ preventScroll: true });

      if (document.activeElement === element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    timer = setTimeout(attempt, PENDING_FOCUS_INTERVAL_MS);
  };

  cancelPendingFocus = stop;
  // The user acting again (a click, a key) ends the request, so it never fights them.
  document.addEventListener('pointerdown', stop, true);
  document.addEventListener('keydown', stop, true);

  attempt();
};

const identity = <T>(node: T) => node as unknown as HTMLElement;

// React Hook Form focuses an invalid field by calling focus() on whatever its `field.ref` received.
// A Joy root wrapper is a div whose focus() does nothing, and RHF still stops looking after it, so
// each control hands over the element the user actually types into. The element is resolved at
// focus time, so a target that is swapped after mount (Quill, the checked radio) is still found.
export const toFieldFocusRef =
  <T = HTMLElement>(
    fieldRef: RefCallBack,
    getElement: (node: T) => HTMLElement | null | undefined = identity
  ): RefCallback<T> =>
  (node) => {
    if (!node) {
      return;
    }

    fieldRef({
      focus: () => focusAndCenter(getElement(node)),
      select: () => {
        const element = getElement(node);

        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
          element.select();
        }
      },
    });
  };

const FOCUS_NAME_ATTRIBUTE = 'data-focus-name';

const byDocumentOrder = (a: Element, b: Element) => {
  if (a === b) {
    return 0;
  }

  return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
};

const escapeAttributeValue = (value: string) => value.replace(/["\\]/g, '\\$&');

export interface IInvalidField {
  path: string;
  message?: string;
  element: HTMLElement;
  focus: () => void;
}

export type FormRoot = Document | Element;

const getFieldError = (errors: FieldErrors, path: string) =>
  get(errors, path) as unknown as FieldError | undefined;

// The screen position comes from the field's named element: RHF wraps the ref a control hands it,
// so the ref cannot lead back to the element. The focus still goes through the ref.
const findInvalidFieldByPath = (
  errors: FieldErrors,
  path: string,
  root: FormRoot
): Omit<IInvalidField, 'path' | 'message'> | undefined => {
  const ref = getFieldError(errors, path)?.ref as { focus?: () => void } | undefined;
  const escapedPath = escapeAttributeValue(path);

  // A control that cannot give RHF a ref (MultiSelectChipList: its value is a field array, its
  // picker has another name) marks its container with data-focus-name instead.
  const container = root.querySelector<HTMLElement>(`[${FOCUS_NAME_ATTRIBUTE}="${escapedPath}"]`);

  if (container) {
    return {
      element: container,
      focus: () => focusAndCenter(container.querySelector<HTMLElement>('input, textarea')),
    };
  }

  const named = root.querySelector<HTMLElement>(`[name="${escapedPath}"]`);

  if (!named) {
    return undefined;
  }

  return {
    element: named,
    focus: () => (ref?.focus ? ref.focus() : focusAndCenter(named)),
  };
};

// An object field can fail on a key of its value (`part.number` for the part autocomplete named
// `part`), so a path without an element of its own falls back to its nearest named ancestor.
const findInvalidField = (
  errors: FieldErrors,
  path: string,
  root: FormRoot
): Omit<IInvalidField, 'path' | 'message'> | undefined => {
  const segments = path.split('.');

  for (let length = segments.length; length > 0; length--) {
    const field = findInvalidFieldByPath(errors, segments.slice(0, length).join('.'), root);

    if (field) {
      return field;
    }
  }

  return undefined;
};

// Every invalid field shown in `root`, in screen order, fields with a ref and ref-less ones in one
// pass. A field that is not on screen is left out.
export const getInvalidFieldsOnScreen = (
  errors: FieldErrors,
  root: FormRoot = document
): IInvalidField[] =>
  getErrorPaths(errors)
    .map((path) => {
      const field = findInvalidField(errors, path, root);

      return field && { path, message: getFieldError(errors, path)?.message, ...field };
    })
    .filter(Boolean)
    .sort((a, b) => byDocumentOrder(a.element, b.element));

const ASTERISK_SELECTOR = '.MuiFormLabel-asterisk';

const getLabelText = (label: Element | null | undefined): string | undefined => {
  if (!label) {
    return undefined;
  }

  const copy = label.cloneNode(true) as Element;

  copy.querySelectorAll(ASTERISK_SELECTOR).forEach((asterisk) => asterisk.remove());

  return copy.textContent?.trim() || undefined;
};

// The label the user reads next to the field. Never the path: it is not text for the user.
export const getFieldLabel = (element: HTMLElement): string | undefined => {
  const labelledBy = element.getAttribute('aria-labelledby');

  return (
    getLabelText((element as HTMLInputElement).labels?.[0]) ??
    (element.getAttribute('aria-label')?.trim() || undefined) ??
    getLabelText(labelledBy ? document.getElementById(labelledBy) : null) ??
    getLabelText(element.closest('.MuiFormControl-root')?.querySelector('label'))
  );
};

// The banner names the field that gets the focus; schema order only when none is on screen.
// A rule without a message still names its field, by the label shown on screen.
export const getFirstErrorMessageOnScreen = (
  errors: FieldErrors,
  root: FormRoot = document
): string | undefined => {
  const fields = getInvalidFieldsOnScreen(errors, root);
  const [firstField] = fields;

  if (firstField?.message) {
    return firstField.message;
  }

  const firstFieldLabel = firstField && getFieldLabel(firstField.element);

  if (firstFieldLabel) {
    return t('errorRequiredField', { field: firstFieldLabel });
  }

  return fields.find(({ message }) => message)?.message ?? getFirstErrorMessage(errors);
};

// RHF focuses in registration order and skips ref-less fields, so after a failed submit this moves
// the focus to the invalid field shown first. Only right after a submit: on a later errors change
// (re-validation while typing) it would pull the cursor away from the field being edited.
export const focusFirstInvalidField = (errors: FieldErrors, root: FormRoot = document): void => {
  const [firstInvalidField] = getInvalidFieldsOnScreen(errors, root);
  // While a request waits for a picker popup to go, its target has not taken the focus yet.
  const focusedElement = isHoldingForPopup ? lastFocusTarget : document.activeElement;

  if (!firstInvalidField || firstInvalidField.element.contains(focusedElement)) {
    return;
  }

  // A field without a named element (Quill's editor) is not a candidate, so when RHF has focused
  // one shown above the first candidate, it keeps the focus.
  const isRhfFocusAbove =
    null !== focusedElement &&
    focusedElement === lastFocusTarget &&
    Boolean(
      firstInvalidField.element.compareDocumentPosition(focusedElement) &
        Node.DOCUMENT_POSITION_PRECEDING
    );

  if (isRhfFocusAbove) {
    return;
  }

  firstInvalidField.focus();
};
